import { useCallback, useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { formatShort } from "@/lib/datetime";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { SynonymSuggestButton } from "@/components/search/SynonymSuggest";
import { ChevronLeft, ChevronRight, EyeOff, Loader2, RotateCcw, ScanSearch, SearchX } from "lucide-react";

const addDays = (iso, n) => { const d = new Date(`${iso}T12:00:00`); d.setDate(d.getDate() + n); return d.toISOString().slice(0, 10); };
const dayLabel = (iso) => new Date(`${iso}T12:00:00`).toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" });

function Tally({ items, testid }) {
  return (
    <span data-testid={testid} className="inline-flex flex-wrap gap-1">
      {items.map((t) => <Badge key={t.name} variant="outline" className="rounded-md text-[11px] font-normal text-slate-600">{t.name} ×{t.times}</Badge>)}
    </span>
  );
}

function Stat({ testid, label, value, tone = "text-slate-900" }) {
  return (
    <div data-testid={testid} className="rounded-xl border border-slate-200 bg-white px-4 py-3">
      <div className={`font-display text-2xl font-bold tabular-nums ${tone}`}>{value ?? "—"}</div>
      <div className="text-xs text-slate-500">{label}</div>
    </div>
  );
}

function WordRow({ w, onHide }) {
  const tid = w.key.replace(/[^a-z0-9]+/g, "-");
  return (
    <div data-testid={`sr-word-${tid}`} className="flex flex-wrap items-start gap-3 px-4 py-3.5 border-t border-slate-100 first:border-t-0">
      <div className="flex-1 min-w-[14rem]">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-semibold text-base text-slate-900">{w.words}</span>
          <Badge data-testid={`sr-word-times-${tid}`} className="rounded-md bg-[#B01E28] text-white font-mono text-[11px]">×{w.times}</Badge>
          {w.synonym && <Badge data-testid={`sr-word-is-synonym-${tid}`} variant="outline" className="rounded-md text-[11px] text-emerald-700 border-emerald-300">now a synonym: {w.synonym.join(" = ")}</Badge>}
        </div>
        <div className="mt-1 flex flex-wrap items-center gap-1.5 text-xs text-slate-500">
          <Tally items={w.boxes} testid={`sr-word-boxes-${tid}`} />
          <Tally items={w.roles} testid={`sr-word-roles-${tid}`} />
          {w.with_filters > 0 && <span>· {w.with_filters} with a filter on</span>}
          <span>· last {formatShort(w.last_at)}</span>
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <SynonymSuggestButton testid={`sr-word-synonym-${tid}`} word={w.words} cameFrom="weekly_list" label="Make it a synonym" />
        <button type="button" data-testid={`sr-word-hide-${tid}`} onClick={() => onHide(w)}
          className="inline-flex items-center gap-1.5 rounded-full h-9 px-3 text-xs font-medium border border-slate-200 text-slate-600 hover:border-slate-500 transition-colors">
          <EyeOff className="w-3.5 h-3.5" />Not something we carry
        </button>
      </div>
    </div>
  );
}

function ScanRow({ s }) {
  const tid = s.key.replace(/[^a-z0-9]+/g, "-").slice(0, 40);
  return (
    <div data-testid={`sr-scan-${tid}`} className="flex flex-wrap items-center gap-2 px-4 py-3 border-t border-slate-100 first:border-t-0">
      <span className="font-mono text-sm font-semibold text-slate-900 break-all">{s.words}</span>
      <Badge data-testid={`sr-scan-times-${tid}`} className="rounded-md bg-slate-800 text-white font-mono text-[11px]">×{s.times}</Badge>
      {s.archived_place && <Badge data-testid={`sr-scan-archived-${tid}`} variant="outline" className="rounded-md text-[11px] text-amber-700 border-amber-300">an archived place</Badge>}
      <Tally items={s.boxes} testid={`sr-scan-boxes-${tid}`} />
      <Tally items={s.roles} testid={`sr-scan-roles-${tid}`} />
      <span className="text-xs text-slate-400">last {formatShort(s.last_at)}</span>
    </div>
  );
}

function NotCarried({ items, onRestore }) {
  const [open, setOpen] = useState(false);
  if (!items.length) return null;
  return (
    <div data-testid="sr-not-carried" className="rounded-xl border border-slate-200 bg-slate-50/60">
      <button type="button" data-testid="sr-not-carried-toggle" onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-4 h-12 text-sm font-semibold text-slate-600">
        <span>Marked "not something we carry" ({items.length})</span>
        <ChevronRight className={`w-4 h-4 transition-transform ${open ? "rotate-90" : ""}`} />
      </button>
      {open && items.map((n) => (
        <div key={n.id} data-testid={`sr-not-carried-${n.id}`} className="flex items-center gap-3 px-4 py-2.5 border-t border-slate-200 text-sm">
          <span className="flex-1 font-medium text-slate-800">{n.words}</span>
          <span className="text-xs text-slate-400">{n.by} · {formatShort(n.at)}</span>
          <Button data-testid={`sr-not-carried-restore-${n.id}`} variant="outline" onClick={() => onRestore(n)} className="h-9 rounded-lg text-xs">
            <RotateCcw className="w-3.5 h-3.5 mr-1" />Put back
          </Button>
        </div>
      ))}
    </div>
  );
}

export function SearchRecordWeek() {
  const [start, setStart] = useState("");
  const [data, setData] = useState(null);
  const [hidden, setHidden] = useState([]);
  const load = useCallback(() => {
    api.get("/admin/search-record/week", { params: start ? { start } : {} }).then(({ data: d }) => setData(d))
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));
    api.get("/admin/search-record/not-carried").then(({ data: d }) => setHidden(d.items || [])).catch(() => {});
  }, [start]);
  useEffect(() => { load(); }, [load]);
  const hide = async (w) => {
    try { await api.post("/admin/search-record/not-carried", { words: w.words }); toast.success(`"${w.words}" won't show on later lists`); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const restore = async (n) => {
    try { await api.post(`/admin/search-record/not-carried/${n.id}/restore`); toast.success(`"${n.words}" is back on the list`); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  if (!data) return <div className="p-10 text-center text-slate-400"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>;
  const thisWeek = data.week_start >= data.this_week_start;
  return (
    <div className="space-y-6" data-testid="sr-week">
      <div className="flex items-center gap-2">
        <Button data-testid="sr-week-prev" variant="outline" disabled={data.week_start <= data.earliest_week}
          onClick={() => setStart(addDays(data.week_start, -7))} className="h-11 w-11 p-0 rounded-lg"><ChevronLeft className="w-4 h-4" /></Button>
        <div data-testid="sr-week-label" className="font-display font-bold text-slate-800 px-2">
          {dayLabel(data.week_start)} – {dayLabel(data.week_end)}{thisWeek ? " · this week" : ""}
        </div>
        <Button data-testid="sr-week-next" variant="outline" disabled={thisWeek}
          onClick={() => setStart(addDays(data.week_start, 7))} className="h-11 w-11 p-0 rounded-lg"><ChevronRight className="w-4 h-4" /></Button>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Stat testid="sr-stat-searches" label="searches typed" value={data.searches} />
        <Stat testid="sr-stat-nothing" label="found nothing" value={data.found_nothing} tone="text-[#B01E28]" />
        <Stat testid="sr-stat-scans" label="scans that found nothing" value={data.scan_misses} tone="text-[#B45309]" />
        <Stat testid="sr-stat-hidden" label={'found nothing, marked "not carried"'} value={data.hidden_not_carried} tone="text-slate-400" />
      </div>
      <section>
        <h2 className="text-base md:text-lg font-display font-bold text-slate-800 mb-2 flex items-center gap-2"><SearchX className="w-5 h-5 text-[#B01E28]" />Found nothing</h2>
        <div data-testid="sr-words" className="rounded-xl border border-slate-200 bg-white overflow-hidden">
          {data.words.length ? data.words.map((w) => <WordRow key={w.key} w={w} onHide={hide} />)
            : <div data-testid="sr-words-empty" className="p-6 text-sm text-slate-400 text-center">No search found nothing this week.</div>}
        </div>
        {data.words_total > data.words_shown && <p data-testid="sr-words-more" className="mt-2 text-xs text-slate-500">Showing the {data.words_shown} most-searched of {data.words_total} words.</p>}
      </section>
      <section>
        <h2 className="text-base md:text-lg font-display font-bold text-slate-800 mb-2 flex items-center gap-2"><ScanSearch className="w-5 h-5 text-[#B45309]" />Scans that found nothing</h2>
        <p className="text-xs text-slate-500 mb-2">A damaged label, a tag nobody made, or a place that was archived.</p>
        <div data-testid="sr-scans" className="rounded-xl border border-slate-200 bg-white overflow-hidden">
          {data.scans.length ? data.scans.map((s) => <ScanRow key={s.key} s={s} />)
            : <div data-testid="sr-scans-empty" className="p-6 text-sm text-slate-400 text-center">No scan found nothing this week.</div>}
        </div>
        {data.scans_total > data.scans_shown && <p data-testid="sr-scans-more" className="mt-2 text-xs text-slate-500">Showing the {data.scans_shown} most-scanned of {data.scans_total} codes.</p>}
      </section>
      <NotCarried items={hidden} onRestore={restore} />
    </div>
  );
}
